import {
  FemsBillingGet200ResponseInner,
  FemsCompareTariffPlanGet200Response,
  FemsCompareTariffPlanVariableTotalEnergyGet200ResponseInner,
  FemsHourlyGet200ResponseInner,
  FemsUsageAgainstTargetGet200Response,
} from "@sizlcorp/sizl-api-document/dist/models";
import { CertificationPredictionAccuracyGet200ResponseInner } from "@sizlcorp/sizl-fems-api-document/dist/models/src/model";
import { AxiosResponse } from "axios";

// 목표 대비 실적
export const targetData = (data: FemsUsageAgainstTargetGet200Response | undefined) =>
  data
    ? {
        totalEnergy: Number(data.totalEnergy),
        maxPower: Number(data.maxPower),
        cost: Number(data.cost),
        carbonEmission: Number(data.carbonEmission),
        toe: Number(data.toe),
        targetEnergyConsumption: Number(data.targetEnergyConsumption),
        targetCarbonEmission: Number(data.targetCarbonEmission),
        consumptionEnergyRatio: Number(data.consumptionEnergyRatio),
        carbonEmissionRatio: Number(data.carbonEmissionRatio),
      }
    : null;

// 월별청구요금
export const mapBillingData = (data: FemsBillingGet200ResponseInner[] | undefined) =>
  data?.map((item) => ({
    year: item.year,
    month: item.month,
    contractPower: Number(item.contractPower),
    billApplyPower: Number(item.billApplyPower),
    totalEnergy: Number(item.totalEnergy),
    leadingPowerFactor: Number(item.leadingPowerFactor),
    laggingPowerFactor: Number(item.laggingPowerFactor),
    billAmount: Number(item.billAmount),
    currentYearBill: Number(item.currentYearBill),
    previousYearBill: Number(item.previousYearBill),
    difference: Number(item.difference),
    errorMessage: item.errorMessage,
  }));

// 요금제 비교
export const mapCompareData = (
  data: AxiosResponse<FemsCompareTariffPlanGet200Response, any> | undefined,
) => ({
  customerNo: data?.data?.customerNo,
  contractPower: data?.data?.contractPower,
  maxPower: data?.data?.maxPower,
  totalEnergy: data?.data?.totalEnergy,
  currentTariffPlan: data?.data?.currentTariffPlan,
  targetTariffPlan: data?.data?.targetTariffPlan,
});

// 요금제 비교 그래프
export const mapCompareVariableData = (
  data: FemsCompareTariffPlanVariableTotalEnergyGet200ResponseInner[] | undefined,
) =>
  data?.map &&
  data?.map((compare, index) => ({
    totalEnergy: Number(compare.totalEnergy),
    maxPower: Number(compare.maxPower),
    currentCharge: Number(compare.currentCharge),
    targetCharge: Number(compare.targetCharge),
    difference: Number(compare.difference),
    state: compare.maxPower,
  }));

// 요금제 비교 그래프 (totalEnergy)
export const mapCompareTotalEnergyData = (
  data: FemsCompareTariffPlanVariableTotalEnergyGet200ResponseInner[] | undefined,
) =>
  data?.map &&
  data?.map((compare, index) => ({
    totalEnergy: Number(compare.totalEnergy),
    maxPower: Number(compare.maxPower),
    currentCharge: Number(compare.currentCharge),
    targetCharge: Number(compare.targetCharge),
    difference: Number(compare.difference),
    state: compare.totalEnergy,
  }));
// 공통 매핑 함수
const mapField =
  (field: keyof FemsHourlyGet200ResponseInner) =>
  (data: FemsHourlyGet200ResponseInner[] | undefined) =>
    data?.map((item) => Number(item[field])) ?? [];

export const mapTotalEnergy = mapField("totalEnergy");
export const mapCarbonEmission = mapField("carbonEmission");
export const mapCost = mapField("cost");
export const mapMaxPower = mapField("maxPower");
export const mapToe = mapField("toe");

const mapFieldObject =
  <T extends keyof FemsHourlyGet200ResponseInner>(
    fields: T[],
    numericFields: T[] = [], // 숫자로 변환할 필드 목록
  ) =>
  (data: FemsHourlyGet200ResponseInner[] | undefined) =>
    data?.map((item) =>
      fields.reduce(
        (acc, field) => ({
          ...acc,
          [field]: numericFields.includes(field) ? Number(item[field]) : item[field],
        }),
        {} as Partial<FemsHourlyGet200ResponseInner>,
      ),
    ) ?? [];

const mapESGFieldObject =
  <T extends keyof CertificationPredictionAccuracyGet200ResponseInner>(
    fields: T[],
    numericFields: T[] = [], // 숫자로 변환할 필드 목록
  ) =>
  (data: CertificationPredictionAccuracyGet200ResponseInner[] | undefined) =>
    data?.map((item) =>
      fields.reduce(
        (acc, field) => ({
          ...acc,
          [field]: numericFields.includes(field) ? Number(item[field]) : item[field],
        }),
        {} as Partial<CertificationPredictionAccuracyGet200ResponseInner>,
      ),
    ) ?? [];

export const mapLoadTypeDayCostData = mapFieldObject(["loadType", "day", "cost"], ["cost"]);
export const mapLoadTypeDayConsumptionMaxPowerCostData = mapFieldObject(
  ["loadType", "day", "month", "totalEnergy", "maxPower", "cost"],
  ["totalEnergy", "maxPower", "cost"],
);
export const mapMonthToeTotalEnergy = mapFieldObject(
  ["month", "totalEnergy", "toe"],
  ["totalEnergy", "toe"],
);
export const mapYearCostTotalEnergy = mapFieldObject(
  ["totalEnergy", "cost"],
  ["totalEnergy", "cost"],
);
export const mapQuarterHourly = mapFieldObject(
  ["quarterHour", "totalEnergy", "carbonEmission", "maxPower"],
  ["totalEnergy", "carbonEmission", "maxPower"],
);
export const mapDailyData = mapFieldObject(
  ["day", "maxPower", "totalEnergy"],
  ["maxPower", "totalEnergy"],
);
export const mapQuarterMaxPower = mapFieldObject(["quarterHour", "maxPower"], ["maxPower"]);
export const mapRangeData = mapFieldObject(
  ["totalEnergy", "maxPower", "cost", "carbonEmission", "range"],
  ["totalEnergy", "maxPower", "cost", "carbonEmission"],
);
export const mapLoadTypeData = mapFieldObject(
  ["loadType", "totalEnergy", "hour", "range", "cost"],
  ["totalEnergy", "cost"],
);

export const equipmentData = mapFieldObject(
  ["totalEnergy", "day", "equipmentCode"],
  ["totalEnergy"],
);

export const mapPredictionAccuracyData = mapESGFieldObject(
  ["totalEnergy", "carbonEmission", "predictionTotalEnergy", "predictionCarbonEmission", "day"],
  ["carbonEmission", "totalEnergy", "predictionTotalEnergy", "predictionCarbonEmission"],
);

export const mapAnomalyCountData = mapESGFieldObject(["anomalyCount", "range"], ["anomalyCount"]);
